import type { JSX, ReactNode } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import { cn } from '@/lib/utils';

/**
 * Several related setup pages under one nav item, as tabs kept in `?tab=` so
 * a tab can be linked to directly. Each tab renders an existing page as-is.
 */
export interface SectionTab {
  id: string;
  label: string;
  element: ReactNode;
}

export function TabbedSection({ title, tabs }: { title: string; tabs: SectionTab[] }): JSX.Element {
  const [{ tab }, set] = useUrlState({ tab: tabs[0]!.id });
  const active = tabs.find((t) => t.id === tab) ?? tabs[0]!;
  return (
    <div className="space-y-5">
      <div role="tablist" aria-label={title} className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={t.id === active.id}
            aria-controls={`panel-${t.id}`}
            onClick={() => set({ tab: t.id })}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
              t.id === active.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`}>
        {active.element}
      </div>
    </div>
  );
}
